import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SEO } from '../components/seo/SEO';
import { client } from '../lib/sanity';
import { ChevronLeft } from 'lucide-react';
import { ShareButtons } from '../components/common/ShareButtons';
import { getPetFullUrl } from '../lib/petUtils';
import type { Pet } from '../types/sanity';
import { PetGallery } from '../components/pet/PetGallery';
import { PetDetails } from '../components/pet/PetDetails';
import { AdoptionButtons } from '../components/pet/AdoptionButtons';
import { SimilarPets } from '../components/pet/SimilarPets';
import { getPetSchema } from '../lib/jsonLd';
import { urlForOptimized } from '../lib/sanity';
import { PayUPayment } from '../components/payments';

export function PetPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pet, setPet] = React.useState<Pet | null>(null);
  const [similarPets, setSimilarPets] = React.useState<Pet[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchPet = async () => {
      try {
        const result = await client.fetch<Pet>(
          `*[_type == "pet" && (slug.current == $identifier || _id == $identifier) && !(_id in path("drafts.**"))][0] {
            _id,
            name,
            slug,
            birthDate,
            subCategory,
            mainCategory,
            gender,
            size,
            status,
            image,
            description,
            isUrgentFundraising,
            fundraisingGoal,
            fundraisingCurrent,
            fundraisingDescription,
            deceasedDate,
            deceasedDescription,
            gallery,
            "tags": tags[]-> {
              _type,
              _ref,
              label,
              "slug": slug
            },
            "shelterMates": shelterMates[]-> {
              _id,
              name,
              image,
              slug
            }
          }`,
          { identifier: slug }
        );

        if (!result) {
          setError('Nie znaleziono zwierzaka');
          setLoading(false);
          return;
        }

        setPet(result);
        
        // Fetch similar pets
        if (result) {
          try {
            const similar = await client.fetch<Pet[]>(
              `*[_type == "pet" && _id != $currentId && status == "available" && !(_id in path("drafts.**"))] | order(publishedAt desc)[0...6] {
                _id,
                name,
                slug,
                birthDate,
                mainCategory,
                gender,
                size,
                status,
                image,
                description
              }`,
              { currentId: result._id }
            );
            setSimilarPets(similar);
          } catch (err) {
            console.error('Error fetching similar pets:', err);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching pet:', err);
        setError('Nie udało się załadować danych zwierzaka');
        setLoading(false);
      }
    };

    if (slug) {
      fetchPet();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <p className="text-center text-xl text-accessible-gray-600 dark:text-accessible-gray-300">
          Ładowanie...
        </p>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <p className="text-center text-xl text-accessible-red-600">
          {error || 'Nie znaleziono zwierzaka'}
        </p>
      </div>
    );
  }

  const petImage = pet.image
    ? urlForOptimized(pet.image, 1200, 800)
    : '/images/animals/fundacja-znajdki-zwierzeta-001.webp';
    
  const petUrl = getPetFullUrl(pet);
  const petDescription = `Poznaj ${pet.name} i pomóż nam znaleźć dla ${pet.mainCategory === 'dog' ? 'niego' : 'niej'} nowy dom. ${pet.description}`;
  
  const jsonLd = getPetSchema(pet);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
      <SEO 
        title={pet.name}
        description={petDescription}
        url={`/zwierze/${pet.slug?.current || ''}`}
        image={petImage}
        jsonLd={jsonLd}
      />

      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-accessible-gray-600 hover:text-accessible-gray-800 dark:text-accessible-gray-300 dark:hover:text-white mb-8 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        Powrót
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="space-y-4">
          <PetGallery
            name={pet.name}
            mainImage={pet.image}
            gallery={pet.gallery}
            gender={pet.gender}
            mainCategory={pet.mainCategory}
          />
        </div>

        <div>
          <PetDetails pet={pet} />
          <AdoptionButtons status={pet.status} mainCategory={pet.mainCategory} />
          <div className="mt-6 pt-6 border-t border-accessible-gray-100 dark:border-accessible-gray-700 high-contrast:border-white/20">
            <ShareButtons
              url={getPetFullUrl(pet)}
              title={`Poznaj ${pet.name} – ${pet.mainCategory === 'dog' ? 'pies' : 'kot'} do adopcji`}
              description={pet.description}
            />
          </div>
        </div>
      </div>

      {/* Sekcja wsparcia finansowego pod głównym kontenerem */}
      <div className="mt-20">
        <h2 className="heading-3 mb-6 text-center">Wesprzyj tego podopiecznego</h2>
        <div className="w-full max-w-xl lg:max-w-2xl mx-auto">
          <PayUPayment
            donationTarget={pet.name}
            petImageUrl={petImage}
            hideImage={true}
          />
        </div>
      </div>

      <SimilarPets pets={similarPets} currentPetId={pet._id} />
    </div>
  );
}